import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'
import { Button } from '@/components/shadcn/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Switch } from '@/components/shadcn/switch'
import { Textarea } from '@/components/shadcn/textarea'
import { FileUploadField } from '@/components/form-components/FileUploadField'

type SkillSummary = RestSchemas['Messages.Skill.SkillSummary']
type NewSkill = RestSchemas['Messages.Skill.NewSkill']
type UpdateSkill = RestSchemas['Messages.Skill.UpdateSkill']

function getSkillErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message || 'Unknown error'
  }

  if (typeof error === 'string') {
    const message = error.trim()
    if (!message) return 'Unknown error'

    try {
      return getSkillErrorMessage(JSON.parse(message))
    } catch {
      return message
    }
  }

  if (error && typeof error === 'object') {
    const errorRecord = error as Record<string, unknown>
    for (const key of ['message', 'Message', 'detail', 'title', 'error']) {
      const nestedMessage = errorRecord[key]
      if (nestedMessage !== undefined && nestedMessage !== error) {
        const message = getSkillErrorMessage(nestedMessage)
        if (message !== 'Unknown error') return message
      }
    }
  }

  return 'Unknown error'
}

const formSchema = z.object({
  key: z
    .string()
    .min(1, 'Key is required')
    .max(64, 'Key cannot exceed 64 characters')
    .regex(/^[a-z0-9_-]+$/, 'Use lowercase letters, numbers, underscores, or hyphens'),
  name: z.string().min(1, 'Name is required').max(120, 'Name cannot exceed 120 characters'),
  description: z.string().max(2000, 'Description cannot exceed 2000 characters').optional().default(''),
  isPublic: z.boolean().default(false),
  packageFile: z
    .custom<File | null>(
      (value) => value === null || (typeof File !== 'undefined' && value instanceof File),
      'Please select a valid ZIP file'
    )
    .optional(),
})

export type TSkillForm = z.infer<typeof formSchema>

type SkillFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedSkill?: SkillSummary
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function SkillForm(props: SkillFormProps) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const isControlled = props.open !== undefined
  const isOpen = props.open ?? sheetOpen
  const edit = !!props.selectedSkill

  const { getSkillApiGetSkills, postSkillApiUpdateSkill, putSkillApiAddSkill, postSkillApiUploadSkillPackage } =
    useRestClient()

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!isControlled) {
        setSheetOpen(open)
      }
      props.onOpenChange?.(open)
    },
    [isControlled, props]
  )

  const invalidateSkills = useCallback(() => {
    getSkillApiGetSkills.invalidateQueries(undefined)
  }, [getSkillApiGetSkills])

  const { mutateAsync: updateSkill, isPending: isUpdating } = postSkillApiUpdateSkill.useMutation()
  const { mutateAsync: addSkill, isPending: isAdding } = putSkillApiAddSkill.useMutation()
  const { mutateAsync: uploadSkillPackage, isPending: isUploadingPackage } =
    postSkillApiUploadSkillPackage.useMutation()

  const finishSkillSave = useCallback(
    async (
      skill: SkillSummary,
      packageFile: File | null | undefined,
      successMessage: string,
      uploadErrorMessage: string
    ) => {
      if (packageFile) {
        if (skill.id == null) {
          invalidateSkills()
          toast.error(uploadErrorMessage)
          return
        }

        try {
          await uploadSkillPackage({
            path: { skillId: skill.id },
            body: { file: packageFile },
          })
        } catch (error) {
          invalidateSkills()
          toast.error(`${uploadErrorMessage}: ${getSkillErrorMessage(error)}`)
          return
        }
      }

      invalidateSkills()
      handleOpenChange(false)
      toast.success(successMessage)
      props.onSubmit?.()
    },
    [handleOpenChange, invalidateSkills, props, uploadSkillPackage]
  )

  const submitHandler = useCallback(
    async (data: TSkillForm) => {
      if (!edit && !data.packageFile) {
        toast.error('A ZIP package is required when creating a skill')
        return
      }

      try {
        if (edit) {
          if (props.selectedSkill?.id == null) {
            toast.error('Unable to update skill: missing skill ID')
            return
          }

          const updatedSkill: UpdateSkill = {
            id: props.selectedSkill.id,
            key: data.key,
            name: data.name,
            description: data.description || null,
            isPublic: data.isPublic,
            contentHash: props.selectedSkill.contentHash ?? '',
            storagePath: props.selectedSkill.storagePath ?? '',
          }
          const savedSkill = await updateSkill({ body: updatedSkill })
          await finishSkillSave(
            savedSkill,
            data.packageFile,
            'Skill successfully updated',
            'Skill updated, but the package upload failed'
          )
          return
        }

        const newSkill: NewSkill = {
          key: data.key,
          name: data.name,
          description: data.description || null,
          isPublic: data.isPublic,
        }
        const savedSkill = await addSkill({ body: newSkill })
        await finishSkillSave(
          savedSkill,
          data.packageFile,
          'Skill successfully created',
          'Skill created, but the package upload failed'
        )
      } catch {
        toast.error(edit ? 'Error updating skill' : 'Error creating skill')
      }
    },
    [addSkill, edit, finishSkillSave, props.selectedSkill, updateSkill]
  )

  const isLoading = isUpdating || isAdding || isUploadingPackage
  if (isLoading) return <FormComponentLoading />

  const showSheet = Boolean(props.sheetTrigger || props.openInSheet || isControlled)
  const sheetTitle = props.sheetTitle ?? (edit ? 'Edit skill' : 'New skill')
  const sheetDescription =
    props.sheetDescription ??
    (edit ? 'Update skill metadata and visibility.' : 'Create a reusable agent skill definition.')

  return showSheet ? (
    <SheetWithFormComponent
      title={sheetTitle}
      description={sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
    >
      <FormComponent selectedSkill={props.selectedSkill} submitHandler={submitHandler} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent selectedSkill={props.selectedSkill} submitHandler={submitHandler} />
  )
}

function FormComponent({
  selectedSkill,
  submitHandler,
}: {
  selectedSkill?: SkillSummary
  submitHandler: (data: TSkillForm) => void | Promise<void>
}) {
  const edit = !!selectedSkill

  const defaultValues = useMemo<TSkillForm>(
    () => ({
      key: selectedSkill?.key ?? '',
      name: selectedSkill?.name ?? '',
      description: selectedSkill?.description ?? '',
      isPublic: selectedSkill?.isPublic ?? false,
      packageFile: null,
    }),
    [selectedSkill]
  )

  const form = useForm<TSkillForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  useEffect(() => {
    form.reset(defaultValues)
  }, [defaultValues, form])

  const handleInvalidSubmit = useCallback(() => {
    toast.error('Please fix validation errors before submitting')
  }, [])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submitHandler, handleInvalidSubmit)}>
        <div className="flex h-full flex-col">
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="key"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Key *</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={edit} />
                  </FormControl>
                  <FormDescription>
                    Stable identifier used when attaching this skill to agents. It cannot be changed after creation.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={4} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isPublic"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between gap-3 rounded-md border p-3">
                  <div>
                    <FormLabel>Public skill</FormLabel>
                    <FormDescription>Allow other users to discover and use this skill.</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FileUploadField
              control={form.control}
              name="packageFile"
              label={edit ? 'Skill package ZIP (optional)' : 'Skill package ZIP *'}
              acceptedFormats={['.zip']}
              maxFiles={1}
            />
          </div>
          <div className="form-action-container">
            <Button type="submit">{edit ? 'Update' : 'Create'}</Button>
          </div>
        </div>
      </form>
    </Form>
  )
}
